import {
  Breakpoints,
  css,
  styled,
  Text,
  TextTag,
  TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import { ArrowDown } from '@volkswagen-onehub/icons-core';
import { keyframes } from 'styled-components';

import { isMissingOneHubMenu } from '@/util/oneHub-website';

interface StyledSectionProps {
  authorHeight?: string;
}

/*
 * The OneHub header's actual rendered box is 65px:
 * 64px visual header height plus its 1px bottom border.
 */
const ONE_HUB_HEADER_HEIGHT = '65px';

const EXTERNAL_HEADER_HEIGHT = isMissingOneHubMenu ? '0px' : ONE_HUB_HEADER_HEIGHT;

const PORTRAIT_GRADIENT = `linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 60.17%,
    rgba(0, 0, 0, 0.7) 97.2%
)`;

const iconBounce = keyframes`
  0% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(14px);
  }

  100% {
    transform: translateY(0);
  }
`;

export const Direction = {
  LTR: 'ltr',
  RTL: 'rtl',
} as const;

export type DirectionValues = (typeof Direction)[keyof typeof Direction];

export function createCSSFourValuesPaddingByDirection(
  top: string | 0,
  right: string | 0,
  bottom: string | 0,
  left: string | 0,
  direction: DirectionValues = Direction.LTR,
): string {
  const directionalPaddingRight = direction !== Direction.RTL ? right : left;
  const directionalPaddingLeft = direction !== Direction.RTL ? left : right;

  return `${top} ${directionalPaddingRight} ${bottom} ${directionalPaddingLeft}`;
}

export const ArrowMaskContainer = styled.div.attrs({
  'data-testid': 'full-screen-arrow-mask-container',
})`
  width: ${(props) => props.theme.size.static350};
  height: 56px;
  overflow: visible;

  display: flex;
  align-items: center;
  justify-content: center;
`;

export const StyledSection = styled.section.attrs<StyledSectionProps>({
  'data-testid': 'full-screen-section',
})<StyledSectionProps>`
  --portrait-gradient: ${PORTRAIT_GRADIENT};

  --landscape-gradient: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 30%,
    rgba(0, 0, 0, 0.5) 70.19%,
    rgba(0, 0, 0, 0.7) 100%
  );

  --reduced-gradient: linear-gradient(180deg, rgba(0, 0, 0, 0) 80%, rgba(0, 0, 0, 0.7) 100%);

  --external-header-height: ${EXTERNAL_HEADER_HEIGHT};

  --footer-block-padding: 1.3rem;
  --footer-content-min-height: 56px;

  --footer-min-height: calc(
    var(--footer-content-min-height) + var(--footer-block-padding) + var(--footer-block-padding) +
      1px
  );

  --stage-height: ${({ authorHeight }) =>
    authorHeight ?? 'calc(100vh - var(--external-header-height))'};

  --content-height: calc(var(--stage-height) - var(--footer-min-height));

  position: relative;
  isolation: isolate;

  width: 100%;
  height: var(--stage-height);

  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  overflow-y: scroll;
  overscroll-behavior: auto;
  scrollbar-width: none;

  pointer-events: none;

  ::-webkit-scrollbar {
    display: none;
  }

  @supports (height: 100dvh) {
    --stage-height: ${({ authorHeight }) =>
      authorHeight ?? 'calc(100dvh - var(--external-header-height))'};
  }

  @media (max-width: ${Breakpoints.b560}px),
    (min-height: 321px) and (max-height: ${Breakpoints.b560}px) and (orientation: landscape) {
    overflow-y: hidden;
  }
`;

export const StyledMediaWrapper = styled.div.attrs({
  'data-testid': 'full-screen-media-wrapper',
})`
  position: sticky;
  top: 0;

  width: 100%;
  height: var(--stage-height);

  overflow: hidden;
  pointer-events: auto;

  [data-testid='item-overlay-wrapper'] {
    z-index: unset;

    [data-testid='styled-toolbar'] {
      top: 50%;
      left: 50%;
      z-index: 3;
      transform: translate(-50%, -50%);
    }

    span[data-cy='videoDisclaimerReferences'] {
      bottom: ${(props) => props.theme.size.static250};
    }
  }
`;

export const StyledOverflowWrapper = styled.div.attrs({
  'data-testid': 'full-screen-overflow-wrapper',
})<{ isAuthorMode?: boolean }>`
  position: absolute;

  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;

  pointer-events: none;

  ${({ isAuthorMode }) =>
    isAuthorMode &&
    css`
      justify-content: flex-end;
    `}
`;

export const StyledContentWrapper = styled.div.attrs({
  'data-testid': 'full-screen-content-wrapper',
})<{ isIncreasedContrast?: boolean }>`
  position: relative;

  min-height: var(--content-height);
  flex-grow: 1;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;

  z-index: 1;
  gap: 1rem;

  padding: 0 2.5vw 1rem;

  pointer-events: none;

  background: ${({ isIncreasedContrast }) =>
    isIncreasedContrast ? 'var(--portrait-gradient)' : 'var(--reduced-gradient)'};

  @media (min-width: ${Breakpoints.b960}px) {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;

    gap: unset;
    padding: 0 4.5vw 1rem;

    ${({ isIncreasedContrast }) =>
      isIncreasedContrast &&
      css`
        background: var(--landscape-gradient);
      `}
  }
`;

export const StyledHeadingWrapper = styled.div.attrs({
  'data-testid': 'full-screen-heading-wrapper',
})<{ startAnimation?: boolean }>`
  visibility: ${({ startAnimation }) => (startAnimation ? 'visible' : 'hidden')};

  margin: 0;
  padding: 0 ${({ theme }) => theme.size.static250};

  color: #fff;
  text-align: center;

  pointer-events: auto;

  h1,
  span,
  p,
  div {
    color: inherit;
  }

  @media (min-width: ${Breakpoints.b960}px) {
    text-align: start;
  }
`;

export const StyledFooter = styled.div.attrs({
  'data-testid': 'full-screen-footer',
})`
  position: relative;

  width: 100%;
  min-height: var(--footer-min-height);
  box-sizing: border-box;

  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: stretch;
  column-gap: 1rem;

  z-index: 1;

  padding: var(--footer-block-padding) 4.5vw;

  border-top: 1px solid rgba(255, 255, 255, 0.3);

  background: rgba(0, 0, 0, 0.7);

  pointer-events: none;

  @media (min-width: ${Breakpoints.b960}px) {
    padding-inline: calc(4.5vw + ${({ theme }) => theme.size.static250});
  }
`;

export const StyledFooterContentWrapper = styled.div.attrs({
  'data-testid': 'full-screen-footer-content-wrapper',
})`
  grid-column: 1;
  justify-self: start;
  align-self: stretch;

  min-height: var(--footer-content-min-height);
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  gap: ${({ theme }) => theme.size.static100};

  min-width: 0;
  max-width: 100%;

  pointer-events: none;
`;

export const StyledDisclaimerWrapper = styled(Text).attrs({
  'data-testid': 'full-screen-disclaimer-wrapper',
  appearance: TokenTextAppearance.copy100,
  tag: TextTag.span,
})`
  width: 100%;
  min-width: 0;
  max-width: 100%;

  display: flex;
  align-items: center;

  overflow-wrap: anywhere;

  pointer-events: auto;

  @media (max-width: ${Breakpoints.b560}px),
    (min-height: 321px) and (max-height: ${Breakpoints.b560}px) and (orientation: landscape) {
    overflow: visible;
  }
`;

export const Styled3DStreamButtonWrapper = styled.div.attrs({
  'data-testid': 'full-screen-3d-stream-button-wrapper',
})`
  min-width: 0;
  max-width: 100%;

  display: flex;
  align-items: center;
  justify-content: flex-start;

  z-index: 2;

  pointer-events: auto;
`;

export const StyledScrollPromptWrapper = styled.div.attrs({
  'data-testid': 'full-screen-scroll-prompt-wrapper',
})`
  grid-column: 2;
  justify-self: end;
  align-self: stretch;

  min-height: var(--footer-content-min-height);
  box-sizing: border-box;

  display: flex;
  align-items: center;
  justify-content: flex-end;

  gap: 5px;

  z-index: 1;

  white-space: nowrap;
  pointer-events: none;
`;

export const StyledScrollDownText = styled(Text).attrs({
  'data-testid': 'full-screen-scroll-down-text',
  appearance: TokenTextAppearance.copy100,
  tag: TextTag.span,
})`
  min-width: ${(props) => props.theme.size.static565};

  display: inline-flex;
  align-items: center;

  line-height: 1;

  @media (max-width: ${Breakpoints.b560}px) {
    display: none;
  }
`;

export const StyledScrollDownIcon = styled(ArrowDown)<{
  startAnimation?: boolean;
}>`
  display: block;

  width: ${(props) => props.theme.size.static350};
  height: ${(props) => props.theme.size.static350};

  flex: 0 0 auto;

  color: white;

  ${({ startAnimation }) =>
    startAnimation &&
    css`
      animation: ${iconBounce} 1s linear 5;
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const StyledFullscreenStageDisclaimerWrapper = styled.div`
  display: flex;

  padding: ${(props) =>
    createCSSFourValuesPaddingByDirection(
      0,
      props.theme.size.static400,
      props.theme.size.static250,
      0,
      props.theme.direction,
    )};
`;

export const StyledStageImage = styled.img.attrs({
  'data-testid': 'fullscreen-stage-image',
})`
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;
`;

export const StyledStageVideoWrapper = styled.div.attrs({
  'data-testid': 'fullscreen-stage-video-wrapper',
})`
  width: 100%;
  height: 100%;

  pointer-events: none;

  > div {
    width: 100%;
    height: 100%;

    border-radius: 0;

    pointer-events: none;
  }

  video {
    width: 100%;
    height: 100%;

    display: block;

    object-fit: cover;
    pointer-events: none;
  }
`;

export const StyledCameraFlightPlaceholderTextWrapper = styled.div.attrs({
  'data-testid': 'fullscreen-stage-camera-flight-placeholder-wrapper',
})`
  position: absolute;

  right: ${({ theme }) => theme.size.grid001};
  bottom: ${({ theme }) => theme.size.static600};
  left: ${({ theme }) => theme.size.grid001};

  z-index: 2;

  color: #fff;

  pointer-events: none;

  p {
    margin: 0;
    color: inherit;
  }
`;
